import { Address, PartialAddress } from '@dreryk/address-common-api';
import {
   ReferralToPsychiatricHospitalCreateOrUpdateDetails,
   ReferralToPsychiatricHospitalPersonCreateOrUpdateDetails,
} from '@dreryk/egabinet-referral-frontend-api';
import { FirstName, LastName } from '@dreryk/person-name-common-api';

import { Choice } from '@dreryk/watermelon';

import { createReferralDiagnosesPayload } from '../../shared/common.utils';

import {
   GuardianEnteredByUserFormBean,
   ReferralToPsychiatricHospitalFormGuardianSelectBean,
} from './fields/GuardianSection';
import { ReferralToPsychiatricHospitalFormGuardianAddress } from './fields/GuardianSection/guardianSection.types';
import { ReferralToPsychiatricHospitalFormBean } from './referralToPsychiatricHospitalForm.types';

const buildGuardianAddress = (guardianAddress: ReferralToPsychiatricHospitalFormGuardianAddress): Address => {
   return PartialAddress.builder()
      .withBuilding(guardianAddress.building)
      .withTown(guardianAddress.town)
      .withFlat(guardianAddress.flat)
      .withPostcode(guardianAddress.postcode)
      .withCountry(guardianAddress.country!)
      .withStreet(guardianAddress.street)
      .build();
};

const getGuardianCreateOrUpdateDetails = (
   referralToHospitalFormSelectBean: ReferralToPsychiatricHospitalFormGuardianSelectBean | null,
   referralToHospitalFormAddress: ReferralToPsychiatricHospitalFormGuardianAddress,
   referralToHospitalFormGuardianEnteredByUser: GuardianEnteredByUserFormBean
): ReferralToPsychiatricHospitalPersonCreateOrUpdateDetails => {
   if (!referralToHospitalFormSelectBean) {
      return {
         firstName: Choice.withUseDefaultIfNull<FirstName>(null),
         lastName: Choice.withUseDefaultIfNull<LastName>(null),
         address: Choice.withUseDefaultIfNull<Address>(null),
         enteredByUser: false,
      };
   }
   if (referralToHospitalFormSelectBean === 'ENTERED_BY_USER') {
      return {
         firstName: Choice.withUseDefaultIfNull<FirstName>(
            referralToHospitalFormGuardianEnteredByUser.firstName
               ? FirstName.of(referralToHospitalFormGuardianEnteredByUser.firstName)
               : null
         ),
         lastName: Choice.withUseDefaultIfNull<LastName>(
            referralToHospitalFormGuardianEnteredByUser.lastName
               ? LastName.of(referralToHospitalFormGuardianEnteredByUser.lastName)
               : null
         ),
         address: Choice.withUseDefaultIfNull<Address>(buildGuardianAddress(referralToHospitalFormAddress)),
         enteredByUser: true,
      };
   }
   return {
      firstName: Choice.withUseDefaultIfNull(
         referralToHospitalFormSelectBean.firstName ? FirstName.of(referralToHospitalFormSelectBean.firstName) : null
      ),
      lastName: Choice.withUseDefaultIfNull(
         referralToHospitalFormSelectBean.lastName ? LastName.of(referralToHospitalFormSelectBean.lastName) : null
      ),
      address: Choice.withUseDefaultIfNull(buildGuardianAddress(referralToHospitalFormAddress)),
      enteredByUser: false,
   };
};

export const mapReferralToPsychiatricFormBeanToCommandDetails = (
   formData: ReferralToPsychiatricHospitalFormBean
): ReferralToPsychiatricHospitalCreateOrUpdateDetails => {
   return {
      cito: false,
      electronic: false,
      unitNameAndAddress: Choice.withUseDefaultIfNull(formData.unitNameAndAddress ?? null),
      diagnoses: createReferralDiagnosesPayload(formData.diagnoses.value),
      informationAboutPatient: Choice.withUseDefaultIfNull(formData.informationAboutPatient ?? null),
      statutoryRepresentativeConsent: Choice.withUseDefaultIfNull(formData.statutoryRepresentativeConsent),
      justification: Choice.withUseDefaultIfNull(formData.otherJustifyingInformation ?? null),
      statutoryRepresentative: getGuardianCreateOrUpdateDetails(
         formData.statutoryRepresentative,
         formData.statutoryRepresentativeAddress,
         formData.statutoryRepresentativeEnteredByUser
      ),
      statutoryGuardianNotApplicable: formData.statutoryRepresentativeNotApplicable ?? false,
      actualGuardianNotApplicable: formData.actualGuardianNotApplicable ?? false,
      actualGuardian: getGuardianCreateOrUpdateDetails(
         formData.actualGuardian,
         formData.actualGuardianAddress,
         formData.actualGuardianEnteredByUser
      ),
      patientConsent: Choice.withUseDefaultIfNull(formData.patientConsent === 'WITH_CONSENT'),
   };
};
